/**
 * Retention status shared by the Retention page badge and the article navbar badge (SNAP-2265).
 *
 * The enum values double as the `status` query values accepted by `GET /retention/items`,
 * so the filter dropdown, the row badge and the article indication can never drift apart.
 */
export enum RetentionStatus {
  EXPIRED = 'expired',
  RETAINED = 'retained',
  LONG_TERM_RETAINED = 'long-term-retained',
}

export const RETENTION_STATUS_LABELS: Record<RetentionStatus, string> = {
  [RetentionStatus.EXPIRED]: 'Expired',
  [RetentionStatus.RETAINED]: 'Retained',
  [RetentionStatus.LONG_TERM_RETAINED]: 'Workspace retained',
};

export const RETENTION_STATUS_HINTS: Record<RetentionStatus, string> = {
  [RetentionStatus.EXPIRED]: 'Past the retention period and scheduled for automatic deletion',
  [RetentionStatus.RETAINED]: 'Marked for long-term retention and excluded from automatic deletion',
  [RetentionStatus.LONG_TERM_RETAINED]:
    'Automatically retained: the workspace has Long-Term Retention by default, so its recordings are never auto-deleted',
};

interface RetentionItemLike {
  retention?: { isLongTerm?: boolean } | null;
  effectiveLongTerm?: boolean;
  workspaceEnforcedLongTerm?: boolean;
}

interface ArticleLike {
  retention?: { isLongTerm?: boolean } | null;
}

interface WorkspaceLike {
  retention?: { defaultLongTerm?: boolean } | null;
}

/**
 * Status of a Retention page row. The workspace default wins over the item's own flag,
 * mirroring the backend `status` filters (an item can only be in one of the three).
 */
export const resolveRetentionItemStatus = (item: RetentionItemLike | null | undefined): RetentionStatus => {
  if (item?.workspaceEnforcedLongTerm) return RetentionStatus.LONG_TERM_RETAINED;

  // Old backend responses carry no effectiveLongTerm — fall back to the item flag (SNAP-2246)
  const isRetained = item?.effectiveLongTerm ?? !!item?.retention?.isLongTerm;
  return isRetained ? RetentionStatus.RETAINED : RetentionStatus.EXPIRED;
};

/**
 * Status shown on an opened article. `null` means "nothing to indicate" — the article is
 * subject to the regular policy and the navbar shows no badge. Expiry is not computed
 * client-side (it needs the video dates), so this never returns EXPIRED.
 */
export const resolveArticleRetentionStatus = ({
  article,
  workspace,
}: {
  article: ArticleLike | null | undefined;
  workspace: WorkspaceLike | null | undefined;
}): RetentionStatus | null => {
  if (workspace?.retention?.defaultLongTerm) return RetentionStatus.LONG_TERM_RETAINED;
  if (article?.retention?.isLongTerm) return RetentionStatus.RETAINED;
  return null;
};
